import React, { useEffect, useState } from 'react'
import { Router, Switch, Route, Redirect, Link, useLocation } from 'react-router-dom'

import CoordinatorDashboardPage from './containers/CoordinatorDashboardPage'
import CoordinatorTaskRedirectPage from './containers/CoordinatorTaskRedirectPage'
import SupplierDataTab from 'rest/views/coordinator/supplier/data/Tab'
import SupplierPublicProfile from 'rest/views/coordinator/supplier/Profile'
import SupplierPage from 'containers/SupplierPage'
import SupplierSearchPage from 'containers/SupplierSearchPage'
import CustomersIdlePage from 'containers/CustomersIdlePage'
import CustomersSuccessTeamsPage from 'containers/CustomersSuccessTeamsPage'
import HandoverPage from 'containers/HandoverPage'
import OrderFilesPage from 'containers/OrderFilesPage'
import NotificationsPage from 'containers/NotificationsPage'
import CommandPalette from 'containers/CommandPalette'
import SupplierDemos from 'containers/SupplierProfile/components/Demos'
import { RatesList } from 'containers/SupplierProfile/components/Rates'
import SupplierOnboarding from 'containers/AdminSupplierOnboarding'
import AnnouncementsPage from 'containers/AnnouncementsPage'
import AgenciesPage from 'containers/AgenciesPage'
import Files from 'components/Files'
import OrderDetailsPane from 'containers/CustomerOrdersPage/components/OrderDetailsPane'
import UserSettingsPage from 'containers/UserSettingsPage'
import retake from "containers/ReportPage/RetakeReport/retake";
import OrderPage from 'containers/OrderPage'
import SidebarLayout from 'components/SidebarLayout'
import useCurrentUser from 'hooks/useCurrentUser'
import { Card, Chip, LoadingFull, Table, Button } from '@arnsbogroup/react-voicearchive'
import Aside from 'components/Aside'
import Icon from 'components/Icon'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import SearchPage from 'containers/SearchPage'
import { FormattedDate, FormattedTime } from 'react-intl'
import classNames from 'classnames'
import SupplierInfoPage from 'containers/SupplierInfoPage'
import AddAgency from 'containers/AgenciesPage/components/AddAgency'
import NewUserPage from 'containers/NewUserPage'
import CustomerPage from './containers/CustomerPage'
import AccountingMergeInvoicesPage from 'containers/Accounting/AccountingMergeInvoicesPage'
import AccountingToBePaidTasksPage from "containers/Accounting/AccountingToBePaidTasksPage";
import AccountingDraftOrdersPage from "containers/Accounting/AccountingDraftOrdersPage"
import TaskSearchPage from 'containers/TaskSearchPage'
import CurrentDispatcherModal from 'components/CurrentDispatcherModal'
import NewOrderModal from 'components/NewOrderModal'
import PreviewFile from 'components/PreviewFile'
import NiceModal  from '@ebay/nice-modal-react'

const Span = ({ children }) => <span>{ children }</span>

const OrderStage = ({ stage }) => (
  <Chip size="xs" state={{
    'PENDING': 'info',
    'IN_PROGRESS': 'info',
    'READY': 'success',
    'CLOSED': 'error',
    'AMENDING': 'warning',
  }[stage] ?? undefined}>
    <span className="capitalize">
      { stage ? stage.toLowerCase().replace('_', ' ') : '' }
    </span>
  </Chip>
)

const TasksSummary = ({ tasks = [] }) => {
  let suppliers = {}
  let types = {}

  tasks.map(({ type, supplier: { userid } }) => {
    types[type] = types[type] ? (types[type] + 1) : 1
    suppliers[userid] = userid
  })

  return (
    <div>
      <div className="text-sm">
        { Object.entries(types).reduce((arr, [key, val]) => [...arr, `${key}${val}`], []).join(', ') }
      </div>
      <div className="text-xs truncate hover:text-clip hover:whitespace-wrap" title={Object.keys(suppliers).join('\n')}>
        { Object.keys(suppliers).join(', ') }
      </div>
    </div>
  )
}


const Coordinator = ({ coordinator_email, coordinator_name, coordinator_image }) => (
  <Person email={coordinator_email} name={coordinator_email} image={coordinator_image} />
)

const Person = ({ email, name, image }) => (
  <div className="whitespace-nowrap truncate" title={`${name} (${email})`}>
    <div className="flex items-center w-full truncate justify-center xl:justify-start">
      <div className="shrink-0 h-8 w-8">
        { image ? (
          <img className="h-8 w-8 rounded-full" src={image} alt="" />
        ) : (
          <div className="w-8 h-8 block rounded-full bg-gray-200 flex justify-center items-center">
            <Icon icon="user" />
          </div>
        ) }
      </div>
      <div className="ml-4 hidden xl:block truncate">
        <div className="text-sm font-medium text-gray-900 truncate">
          { name }
        </div>
        <div className="text-sm text-gray-500 truncate">
          { email }
        </div>
      </div>
    </div>
  </div>
)

const Deadline = ({ deadline }) => {
  const value = new Date(deadline * 1000)
  return (
    <div className="space-x-1">
      <span>
        <FormattedDate value={value} />
      </span>
      <span>
        <FormattedTime value={value} />
      </span>
    </div>
  )
}

const CreatedAt = ({ created_at }) => {
  const value = new Date(created_at)
  return (
    <div className="space-x-1">
      <span>
        <FormattedDate value={value} />
      </span>
      <span>
        <FormattedTime value={value} />
      </span>
    </div>
  )
}

const Fallback = () => {
  useEffect(() => {
    window.location = '/'
  }, [])

  return null
}

const CoordinatorApp = ({ history, currentUser, partner }) => {
  const { features = [], routes } = useCurrentUser()


  return (
    <Router history={history}>
      <MaybeSidebarLayout partner={partner} routes={routes}>
        <div className="flex h-full w-full">
          <Route path="/" component={OrderFilesPage} />
          <Route path="/" component={NotificationsPage} />

          <Route path="/" render={({ history, location: { search } }) => {
            const params = new URLSearchParams(search)
            const orderHistoryId = params.get('orderHistoryId')
            params.delete('orderHistoryId')

            return !orderHistoryId ? null : (
              <Aside onClose={() => history.push('?' + params.toString())}>
                <OrderDetailsPane order={{ uid: orderHistoryId }} />
              </Aside>
            )
          }} />

          <Route path="/" render={({ history, location: { search } }) => {
            const params = new URLSearchParams(search)
            const orderId = params.get('orderFilesId')
            params.delete('orderFilesId')
            return !orderId ? null : (
              <Aside onClose={() => history.push('?' + params.toString())}>
                <Files orderId={orderId} />
              </Aside>
            )
          }} />

          <Route path="/suppliers/new" render={() => {
            if (confirm("Are you sure you want to create a new supplier?")) {
              fetch(`/rest/v2/supplier/skeleton`, {
                  method: 'POST',
              })
              .then((response) => response.json())
              .then((response) => {
                  if (response.success) {
                      history.push(`/suppliers/${response.data.uid}/overview`)
                  } else {
                      toastr.error(response.errors)
                      history.push(`/dashboard`)
                  }
              }).catch((error) => {
                  toastr.error(error)
              })
            } else {
              history.push(`/dashboard`)
            }
          }} />
          <Route path="/customers/new" render={() => {
            if (confirm("Are you sure you want to create a new Company?")) {
              fetch(`/rest/v2/customer/skeleton`, {
                  method: 'POST',
              })
              .then((response) => response.json())
              .then((response) => {
                  if (response.success) {
                      history.push(`/customers/${response.data.uid}/contact-info`)
                  } else {
                      toastr.error(response.errors)
                      history.goBack()
                  }
              }).catch((error) => {
                  toastr.error(error)
                  history.goBack()
              })
            } else {
              history.goBack()
            }
          }} />
          <Route path="/suppliers" render={(props) => (
            <SupplierSearchPage history={history} {...props} />
          )} />
          
          <Route path="/agencies" render={(props) => (
            <div className={ props.match.isExact ? 'w-full' : undefined }>
              {props.match.isExact &&
                <div className='m-2'>
                  <AddAgency />
                </div>
              }
              <SearchPage {...props}
                containerClassName="w-full"
                baseApiUrl="/rest/v2/agency"
                components={{
                  Tr: ({ childProps = {}, ...innerProps }) => {
                    return (
                      <Table.Tr
                        {...innerProps }
                        className={classNames({
                          'cursor-pointer': !!childProps,
                          'bg-blue-50': props?.location?.pathname.startsWith(`/agencies/${childProps?.uid}`),
                        })}
                        onClick={childProps.uid ? () => history.push(`/agencies/${childProps.uid}/members`) : undefined}
                      />
                    )
                  },
                  Td: ({ childProps = {}, ...props }) => (
                    <Table.Td
                      {...props }
                    />
                  )
                }}
                head={{
                  uid: { className: 'w-24', label: 'ID' },
                  username: { label: 'Contact Person' },
                  name: { label: 'Name' },
                  country: { label: 'Country' },
                  email: { label: 'Email' },
                }}
                body={!props.match.isExact ? {
                  uid: Span,
                  username: Span,
                } : {
                  uid: Span,
                  username: Span,
                  name: Span,
                  country: Span,
                  email: Span,
                }}
              />
            </div>
              
          )} />

          <Route path="/customers" render={(props) => (
            <SearchPage {...props}
              baseApiUrl="/rest/v2/customer"
              components={{
                Tr: ({ childProps, ...innerProps }) => (
                  <Table.Tr
                    {...innerProps }
                    className={classNames({
                      'cursor-pointer': !!childProps,
                      'bg-blue-50': props?.location?.pathname.startsWith(`/customers/${childProps?.uid}`),
                    })}
                    onClick={childProps?.uid ? () => history.push(`/customers/${childProps.uid}/contact-info`) : undefined}
                  />
                )
              }}
              head={{
                uid: { sortable: true, className: 'w-20' },
                orders: { sortable: true, className: 'w-20' },
                name: { sortable: true },
                email: { sortable: true },
                orders: { sortable: true },
              }}
              body={!props.match.isExact ? {
                uid: Span,
                name: Span,
              } : {
                uid: Span,
                company_id: ({ company_id }) => (company_id > 0 ?
                  <a
                    onClick={(e) => e.stopPropagation()}
                    href={`https://my.voicearchive.com/crm/company/details/${company_id}/`}
                    target="_blank"
                    className="text-primary hover:text-primary-darker"
                  >
                    { company_id } <Icon icon="external-link" className="text-xs" />
                  </a> : null
                ),
                name: Span,
                country: Span,
                email: Span,
                orders: Span,
                type: Span,
              }}
            />
          )} />
          
          <Route path="/tasks" render={(props) => (
            <TaskSearchPage history={history} {...props} />
          )} />

          <Route path="/orders" render={(props) => (
            <SearchPage {...props}
              baseApiUrl="/rest/v2/order"
              components={{
                Tr: ({ childProps, ...innerProps }) => (
                  <Table.Tr
                    {...innerProps }
                    className={classNames({
                      'cursor-pointer': !!childProps,
                      'bg-blue-50': props?.location?.pathname.startsWith(`/orders/${childProps?.uid}`),
                    })}
                    onClick={childProps?.uid ? () => history.push(`/orders/${childProps.uid}/overview`) : undefined}
                  />
                )
              }}
              head={{
                uid: { className: 'w-24', label: 'ID' },
                deadline: { sortable: true },
                name: { sortable: true },
                customer_name: { label: 'Customer' },
                total: { sortable: true },
              }}
              body={!props.match.isExact ? {
                uid: Span,
                name: Span,
              } : {
                uid: Span,
                stage: OrderStage,
                deadline: Deadline,
                name: Span,
                customer_name: Span,
                total: Span,
                tasks: TasksSummary,
                coordinator: Coordinator,
              }}
            />
          )} />

          <Route path="/handovers" render={(props) => (
            <HandoverPage {...props} />
          )} />

          <Route path="/projects" render={(props) => (
            <SearchPage {...props}
              baseApiUrl="/rest/v2/project"
              head={{
                uid: { className: 'w-24', label: 'ID' },
                hash_id: { className: 'w-40', label: 'Short ID' },
              }}
              body={{
                uid: Span,
                created_at: CreatedAt,
                hash_id: Span,
                href: ({ href }) => <a href={href} className="text-primary" target="_blank">{ href }</a>,
                creator: ({ creator }) => <Person {...creator} />,
              }}
            />
          )} />

          <Route path="/preview/:fileId" render={(props) => (
            <PreviewFile fileId={props.match.params.fileId} {...props} />
          )} />

          <Switch>
            <Route exact path="/" component={CoordinatorDashboardPage} />
            <Route path="/orders/new" render={() => {NiceModal.show(NewOrderModal, { history })}} />,
            <Route path="/orders/:orderId/jobs/:jobId/versions/:versionId/tasks/:taskId" component={OrderPage} />,
            <Route path="/orders/:orderId/jobs/:jobId/versions/:versionId" component={OrderPage} />,
            <Route path="/orders/:orderId/jobs/:jobId" component={OrderPage} />,
            <Route path="/orders/:orderId" render={(props) => (
                  <OrderPage {...props} />
            )}/>,

            <Route path="/task/:taskId" component={CoordinatorTaskRedirectPage} />

            <Route path="/dashboard" component={CoordinatorDashboardPage} />
            <Route path="/settings" component={UserSettingsPage} />

            <Route path="/accounting/merge-invoices" component={AccountingMergeInvoicesPage} />
            <Route path="/accounting/to-be-paid-tasks" component={AccountingToBePaidTasksPage} />
            <Route path="/accounting/accounting-draft-orders" component={AccountingDraftOrdersPage}/>

            <Route path="/suppliers/onboarding" component={SupplierOnboarding} />

            <Route path="/agencies/:agencyId" render={(props) => (
                  <AgenciesPage agencyId={props.match.params.agencyId}/>
            )} />
            
            <Route path="/users/new/:userType" render={(props) => (
                  <NewUserPage newUserType={props.match.params.userType}/>
            )} />
            
            <Route path="/supplier/:supplierId/data" component={SupplierDataTab} />
            <Route path="/supplier/:supplierId/profile" component={SupplierPublicProfile} />
            <Route path="/supplier/:supplierId/rates" component={RatesList} />
            <Route path="/docs" component={SupplierInfoPage} />
            <Route path="/supplier/:supplierId/demos" render={(props) => (
              <SupplierDemos admin {...props} />
            )} />
            <Route path="/report/retake" component={retake}/>
            <Redirect from="/talent/sign-up" to="/suppliers/onboarding" />
            <Route path="/user/sign-in" component={Fallback} />
            <Route path="/user/forgot" component={Fallback} />
            <Route path="/announcements" component={AnnouncementsPage} />
            <Route path="/retool/:uuid" render={({ match: { params: { uuid } } }) => (
              <iframe
                className="w-full h-full"
                src={`https://voicearchive.retool.com/embedded/public/${uuid}`}
              />
            )} />
            <Redirect from="/reload-dashboard" to="/dashboard" />

            <Route path="/reports" render={(props) => (
              <div>
                {Object.entries(routes.reports || {}).map(([section, reports]) => (
                  <div className="p-4 space-y-2">
                    <h1 className="font-medium text-lg">{ section }</h1>
                    <div className="grid grid-cols-4 gap-3">
                      { reports.map(([name, description, to, beta = false]) => (
                        <Link to={to} onClick={(event) => {
                          if (to.endsWith('.csv')) {
                            event.preventDefault()
                            event.stopPropagation()
                            window.open(to, '_blank')
                          }
                        }}>
                          <Card className="p-4 flex justify-between items-center">
                            <div className="space-x-2">
                              <span className="font-medium">{ name }</span>
                              { beta &&
                                <Chip size="xs" state="warning">Beta</Chip>
                              }
                            </div>

                            { to.endsWith('.csv') &&
                                <Chip size="xs" state="info">CSV</Chip>
                            }
                          </Card>
                        </Link>
                      )) }
                    </div>
                  </div>
                )) }
              </div>
            )} />

            <Route path="/suppliers/:id" render={(props) => (
              <div className="w-full h-full flex">
                <div className="w-full">
                  <SupplierPage history={history} supplierId={props.match.params.id} partner={partner}/>
                </div>
              </div>
            )} />

            <Route path="/customers/idle" component={CustomersIdlePage} />
            <Route path="/customers/success-teams" component={CustomersSuccessTeamsPage} />

            <Route path="/customers/:id" render={(props) => (
              <div className="w-full h-full flex">
                <div className="w-full">
                  <CustomerPage customerId={props.match.params.id}/>
                </div>
              </div>
            )} />

            <Route path="/current-dispatcher" render={(props) => {NiceModal.show(CurrentDispatcherModal)}} />
          </Switch>
        </div>
      </MaybeSidebarLayout>
    </Router>
  )
}

const MaybeSidebarLayout = ({ partner, disabled = false, routes, children, ...props }) => {

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {

    fetch(`/rest/v2/handover`)
      .then(response => response.json())
      .then((handovers) => {

        let key = "Currently active";
        if (handovers[key] && handovers[key].length > 0) {
          const newNotification = {
            id: 'handover-alert',
            title: 'Handover Alert',
            body: (handovers[key].length == 1) ? 'There is a handover to assign.' : 'There are handovers to assign.',
            markSeen: null,
            created_at: new Date(),
            url: '/dashboard',
          }
          setNotifications((notifications) => [...notifications, newNotification]);
        }

      })
      .catch((err) => {
        console.error(err)
      })

  }, []);

  if (disabled) {
    return children
  }

  const functionalize = (route) => {
    if (route.routes) {
      route.routes = route.routes.map(functionalize)
    }

    if (Array.isArray(route.onClick)) {
      const [args, body] = route.onClick
      route.onClick = new Function(args, body)
    }

    return route
  }

  return (
    <SidebarLayout
      brand={{
        name: '',
        logo: partner === 'voicetome' ?
        'https://voicetome-branding.s3.eu-central-1.amazonaws.com/logo-white-white.png' :
        'https://voicearchive-branding.s3.eu-central-1.amazonaws.com/small-curve-white.svg',
      }}
      apps={routes?.apps}
      routes={routes?.main.map(functionalize) || []}
      footerRoutes={routes?.footer}
      notifications={notifications}
      CommandPalette={CommandPalette}
      version={window?.app.version || null}
      lastUpdate={window?.app.timestamp || null}
    >
      { children }
    </SidebarLayout>
  )
}

export default CoordinatorApp
